import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  History,
  Wallet,
  LogOut,
  Menu,
  X,
  User,
  Truck,
  DollarSign,
  AlertTriangle,
  Send,
  CreditCard,
  RotateCcw,
  Store,
  Receipt,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  TrendingUp,
  Settings,
  Trophy,
  Crown,
} from "lucide-react";

// Grouped navigation structure
const navigationGroups = [
  {
    id: "principal",
    label: "Principal",
    icon: LayoutDashboard,
    items: [
      { to: "/vendedor", icon: LayoutDashboard, label: "Inicio", end: true },
      { to: "/vendedor/catalogo-pedidos", icon: Store, label: "Catálogo Digital" },
    ]
  },
  {
    id: "ventas",
    label: "Ventas",
    icon: ShoppingCart,
    items: [
      { to: "/vendedor/venta", icon: ShoppingCart, label: "Nueva Venta" },
      { to: "/vendedor/pedidos", icon: ClipboardList, label: "Mis Pedidos" },
      { to: "/vendedor/historial", icon: History, label: "Historial Ventas" },
    ]
  },
  {
    id: "inventario",
    label: "Inventario",
    icon: Package,
    items: [
      { to: "/vendedor/inventario", icon: Package, label: "Mi Inventario" },
      { to: "/vendedor/requisiciones", icon: Truck, label: "Requisiciones" },
    ]
  },
  {
    id: "finanzas",
    label: "Finanzas",
    icon: TrendingUp,
    items: [
      { to: "/vendedor/cierre", icon: Wallet, label: "Cierre de Caja" },
      { to: "/vendedor/desempeno", icon: Trophy, label: "Mis Ganancias" },
      { to: "/vendedor/club", icon: Crown, label: "Club Abuela" },
      { to: "/vendedor/negocios", icon: Store, label: "Negocios TAT" },
    ]
  },
];

// Optional navigation item for gastos (only for vendors with permission)
const gastosNavItem = { to: "/vendedor/gastos", icon: Receipt, label: "Registrar Gastos" };

// Optional navigation item for delivery module (only for vendors with can_deliver_orders)
const deliveryNavItem = { to: "/delivery/dashboard", icon: Truck, label: "Mis Entregas", highlight: true };

// Componente de bloqueo por cierre pendiente
function CierrePendienteBlocker({ pendienteData, onCierreComplete }) {
  const [form, setForm] = useState({
    total_efectivo: pendienteData.resumen?.efectivo_esperado?.toString() || "0",
    total_transferencia: pendienteData.resumen?.transferencia_esperado?.toString() || "0",
    unidades_devueltas: "0",
    notas: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [showResumen, setShowResumen] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const totalReportado =
    (parseFloat(form.total_efectivo) || 0) + (parseFloat(form.total_transferencia) || 0);
  const diferencia = totalReportado - (pendienteData.resumen?.total_ventas || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!showResumen) {
      setShowResumen(true);
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        total_efectivo: parseFloat(form.total_efectivo) || 0,
        total_transferencia: parseFloat(form.total_transferencia) || 0,
        unidades_devueltas: parseInt(form.unidades_devueltas) || 0,
        notas: form.notas || null,
      };

      await api.post("/cierres/pendiente", payload);
      toast.success("¡Cierre pendiente registrado correctamente!");
      onCierreComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar cierre");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-amber-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Warning Header */}
        <div className="bg-amber-100 border-2 border-amber-400 rounded-t-2xl p-6 text-center">
          <AlertTriangle className="w-16 h-16 text-amber-600 mx-auto mb-4" />
          <h1 className="font-display text-2xl text-amber-800 mb-2">
            Cierre de Caja Pendiente
          </h1>
          <p className="text-amber-700">
            {pendienteData.mensaje}
          </p>
        </div>

        {/* Summary Card */}
        <div className="bg-white border-x-2 border-amber-400 p-6">
          <h2 className="font-display text-lg text-torta-dark mb-4">
            Resumen del día {new Date(pendienteData.fecha_pendiente).toLocaleDateString("es-CO")}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-torta-cream rounded-xl p-4">
              <p className="text-xs text-torta-muted">Total Ventas</p>
              <p className="font-display text-xl text-torta-dark">
                {formatCurrency(pendienteData.resumen?.total_ventas)}
              </p>
            </div>
            <div className="bg-torta-cream rounded-xl p-4">
              <p className="text-xs text-torta-muted">Unidades</p>
              <p className="font-display text-xl text-torta-dark">
                {pendienteData.resumen?.unidades_vendidas || 0}
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!showResumen ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      Efectivo
                    </Label>
                    <Input
                      type="number"
                      required
                      value={form.total_efectivo}
                      onChange={(e) => setForm({ ...form, total_efectivo: e.target.value })}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                      Transferencias
                    </Label>
                    <Input
                      type="number"
                      required
                      value={form.total_transferencia}
                      onChange={(e) => setForm({ ...form, total_transferencia: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <RotateCcw className="w-4 h-4 text-amber-600" />
                    Unidades devueltas
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.unidades_devueltas}
                    onChange={(e) => setForm({ ...form, unidades_devueltas: e.target.value })}
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Notas</Label>
                  <Textarea
                    value={form.notas}
                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                    placeholder="Observaciones del día..."
                    rows={2}
                  />
                </div>
              </>
            ) : (
              /* Resumen Pre-Envío */
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                <h3 className="font-medium text-blue-800 text-center mb-4">
                  Confirma tu cierre antes de enviar
                </h3>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Efectivo reportado:</span>
                  <span className="font-medium">{formatCurrency(parseFloat(form.total_efectivo) || 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Transferencias:</span>
                  <span className="font-medium">{formatCurrency(parseFloat(form.total_transferencia) || 0)}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-2">
                  <span className="text-gray-600">Total reportado:</span>
                  <span className="font-bold">{formatCurrency(totalReportado)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total sistema:</span>
                  <span className="font-medium">{formatCurrency(pendienteData.resumen?.total_ventas)}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-2">
                  <span className="text-gray-600">Diferencia:</span>
                  <span className={`font-bold ${
                    diferencia === 0 ? "text-green-600" : diferencia > 0 ? "text-blue-600" : "text-red-600"
                  }`}>
                    {diferencia === 0 ? "✓ Cuadrado" : formatCurrency(diferencia)}
                  </span>
                </div>
                {parseInt(form.unidades_devueltas) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Unidades devueltas:</span>
                    <span className="font-medium text-amber-600">{form.unidades_devueltas}</span>
                  </div>
                )}
                {form.notas && (
                  <div className="text-sm pt-2 border-t">
                    <span className="text-gray-600">Notas:</span>
                    <p className="text-gray-800 mt-1">{form.notas}</p>
                  </div>
                )}
                
                <button
                  type="button"
                  onClick={() => setShowResumen(false)}
                  className="text-sm text-blue-600 hover:underline w-full text-center mt-2"
                >
                  ← Modificar datos
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-torta-brown hover:bg-torta-brown/90"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Enviando...
                </span>
              ) : showResumen ? (
                <span className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Confirmar y Enviar Cierre
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Revisar antes de enviar
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-amber-100 border-2 border-t-0 border-amber-400 rounded-b-2xl p-4 text-center">
          <p className="text-amber-700 text-sm">
            Debes completar este cierre para continuar usando la aplicación
          </p>
        </div>
      </div>
    </div>
  );
}

export default function VendedorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cierrePendiente, setCierrePendiente] = useState(null);
  const [checkingCierre, setCheckingCierre] = useState(true);

  // Check for pending cierre only for "carrito" type vendors
  useEffect(() => {
    const checkCierrePendiente = async () => {
      // Only check for "carrito" type vendors
      if (user?.tipo_vendedor === "digital") {
        setCheckingCierre(false);
        return;
      }
      
      try {
        const response = await api.get("/cierres/pendiente");
        if (response.data.tiene_cierre_pendiente) {
          setCierrePendiente(response.data);
        }
      } catch (error) {
        console.error("Error checking cierre pendiente:", error);
      } finally {
        setCheckingCierre(false);
      }
    };

    checkCierrePendiente();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleCierreComplete = () => {
    setCierrePendiente(null);
  };

  // Show loading while checking
  if (checkingCierre) {
    return (
      <div className="min-h-screen bg-torta-cream flex items-center justify-center">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  // Show blocker for carrito vendors with pending cierre
  if (cierrePendiente && user?.tipo_vendedor !== "digital") {
    return (
      <CierrePendienteBlocker 
        pendienteData={cierrePendiente} 
        onCierreComplete={handleCierreComplete}
      />
    );
  }

  return (
    <div className="min-h-screen bg-torta-cream">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-torta-sand z-40 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-torta-sand rounded-lg"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-torta-brown rounded-full flex items-center justify-center">
            <span className="text-lg">🥧</span>
          </div>
          <span className="font-display text-torta-dark">Vendedor</span>
        </div>
        <NotificationBell />
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-64 bg-torta-sand border-r border-torta-sand/50 z-50 transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-white/50">
            <div className="flex items-center gap-3">
              <img
                src="/logo-torta-cuajada.png"
                alt="Logo"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <h1 className="font-display text-base text-torta-dark">Torta de Cuajada</h1>
                <p className="text-xs text-torta-brown uppercase tracking-wide">Vendedor</p>
              </div>
            </div>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 overflow-y-auto p-3">
            {/* Grouped Navigation */}
            {navigationGroups.map((group) => (
              <div key={group.id} className="mb-3">
                <p className="px-3 py-1 text-xs font-semibold text-torta-brown/70 uppercase tracking-wider">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-torta-brown text-white"
                            : "text-torta-dark hover:bg-white/50"
                        }`
                      }
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}

            {/* Cuenta Section */}
            <div className="mb-3">
              <p className="px-3 py-1 text-xs font-semibold text-torta-brown/70 uppercase tracking-wider">
                Mi Cuenta
              </p>
              <div className="space-y-0.5">
                <NavLink
                  to="/vendedor/perfil"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-torta-brown text-white"
                        : "text-torta-dark hover:bg-white/50"
                    }`
                  }
                >
                  <User className="w-4 h-4" />
                  Mi Perfil
                </NavLink>
                
                {/* Conditional gastos navigation for vendors with permission */}
                {user?.puede_registrar_gastos && (
                  <NavLink
                    to={gastosNavItem.to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-amber-600 text-white"
                          : "text-amber-700 bg-amber-50 hover:bg-amber-100"
                      }`
                    }
                  >
                    <gastosNavItem.icon className="w-4 h-4" />
                    {gastosNavItem.label}
                  </NavLink>
                )}

                {/* Conditional delivery navigation for vendors with delivery permission */}
                {user?.can_deliver_orders && (
                  <NavLink
                    to={deliveryNavItem.to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-blue-600 text-white"
                          : "text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200"
                      }`
                    }
                  >
                    <deliveryNavItem.icon className="w-4 h-4" />
                    {deliveryNavItem.label}
                    <span className="ml-auto text-xs px-1.5 py-0.5 bg-blue-600 text-white rounded-full">
                      Nuevo
                    </span>
                  </NavLink>
                )}
              </div>
            </div>
          </nav>

          {/* User Section - Fixed at bottom */}
          <div className="p-3 border-t border-white/50 bg-torta-sand">
            <div className="flex items-center gap-2 mb-2 px-2">
              <div className="w-8 h-8 bg-torta-brown/20 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-torta-brown" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-torta-dark text-sm truncate">{user?.nombre}</p>
                <p className="text-xs text-torta-muted truncate">{user?.zona || "Sin zona"}</p>
              </div>
              <NotificationBell />
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="w-full justify-center border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-400"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
